import { Modal } from '@components/Modal';
import { Button, Stack } from '@mui/material';
import { theme } from '@src/theme';
import { border, buildDataTestIdAttribute, buildFullDataTestId, scrollBarSM } from '@src/utils';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  ContentLoading,
  Diagnoses,
  LabsSelect,
  OrderingProviderSelect,
  Patients,
  Tests,
  useLoadingContext,
} from './components';
import { defaultValues } from './OrdersCreateModal.consts';
import { schema } from './OrdersCreateModal.schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, memo, useMemo } from 'react';
import { IBulkOrderBody } from '@src/api';

type OrdersCreateModalProps = {
  data: Nullable<number>;
  setOpen: (val: Nullable<number>) => void;
  setConfirmOrdersCreate: (val: { data: IBulkOrderBody; id: number }) => void;
};

export const OrdersCreateModal: FC<OrdersCreateModalProps> = memo(({ data, setOpen, setConfirmOrdersCreate }) => {
  const {
    control,
    getValues,
    watch,
    trigger,
    formState: { isDirty, errors, isValid, ...formState },
    ...rest
  } = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: { ...defaultValues },
    mode: 'onTouched',
  });

  const { loadingState } = useLoadingContext();

  const onCancel = () => {
    setOpen(null);
  };

  const onApply = () => {
    const { laboratoryId, providerId, productsByLoincItems, additionalProductIds, diagnosisIds, patientIds } =
      getValues();
    if (!data) return;
    const body = {
      data: {
        laboratoryId: laboratoryId || '',
        providerId: providerId || null,
        patientIds: patientIds || null,
        additionalProductIds: additionalProductIds?.map(({ id }) => id) || null,
        diagnosisIds: diagnosisIds?.map(({ id }) => id) || null,
        productsByLoincItems: productsByLoincItems
          ? productsByLoincItems.map(({ loincItemId, productId }) => ({ loincItemId, productId: productId || null }))
          : null,
      },
      id: data,
    };
    setConfirmOrdersCreate(body);
    setOpen(null);
  };

  const dataTestId = buildFullDataTestId('modal-create-order');

  const methods = {
    control,
    getValues,
    watch,
    trigger,
    formState: { isDirty, errors, isValid, ...formState },
    ...rest,
  };

  const [patientIds] = watch(['patientIds']);

  const allLoading = useMemo(() => {
    const { labsCount, labs, patients: patientLoading, tests, biomarkers, providers } = loadingState || {};
    return labsCount
      ? labsCount === 1
        ? !(labs && patientLoading && tests && biomarkers && providers)
        : !(labs && patientLoading)
      : true;
  }, [loadingState]);

  if (!data) return null;

  return (
    <Modal
      open={!!data}
      title="Create orders"
      bodySx={{ padding: 0 }}
      onCancel={onCancel}
      wrapperProps={{
        PaperProps: {
          sx: {
            maxWidth: '1680px',
            maxHeight: '900px',
            width: '100%',
            height: '100%',
            position: 'relative',
            overflowY: 'hidden',
          },
          ...buildDataTestIdAttribute(dataTestId),
        },
      }}
    >
      {allLoading && <ContentLoading />}

      <FormProvider {...methods}>
        <Stack direction="row" width="100%" height="calc(100% - 90px)" position="relative">
          <Stack
            width="50%"
            height="100%"
            padding="24px 24px 90px 24px"
            gap="18px"
            overflow="auto"
            sx={{ borderRight: border, ...scrollBarSM }}
          >
            <Stack sx={{ backgroundColor: theme.palette.grey['200'] }} gap="18px" padding="12px 18px">
              <LabsSelect />
              <OrderingProviderSelect />
            </Stack>
            <Tests selectionId={data} />
            <Diagnoses />
          </Stack>
          <Stack width="50%">
            <Patients selectionId={data} />
          </Stack>
        </Stack>
      </FormProvider>
      <Stack
        direction="row"
        justifyContent="space-between"
        padding="24px"
        position="absolute"
        width="100%"
        bgcolor="#fff"
        borderTop={border}
        sx={{ bottom: 0, left: 0, boxShadow: allLoading ? 'none' : '0px -2px 12px 0px #0000001A' }}
      >
        <Button color="secondary" variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={onApply}
          disabled={!isValid || Object.keys(errors).length > 0}
        >
          {`Create ${patientIds?.length || 0} Orders`}
        </Button>
      </Stack>
    </Modal>
  );
});
